import { useMemo, useState } from "react"
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { Loader2, Plane } from "lucide-react"
import { TravelSegmentDetail } from "@/components/travel/TravelSegmentDetail"
import { TravelForm, type TravelFormKind } from "@/components/travel/TravelForm"
import { CanvasHeader } from "@/components/canvas/ViewPlaceholder"
import { useActiveEvent } from "@/core/active-event"
import { buildSegments, eventTravelQueryOptions } from "@/core/travel"

const ADD_KINDS: TravelFormKind[] = ["flight", "ground", "rental"]

// Full-screen travel-movement detail — the narrow-canvas counterpart to the
// travel reading-pane split. The segment id is URL-encoded (it carries route +
// time delimiters), so decode before matching.
export const Route = createFileRoute("/$orgSlug/$eventSlug/m/$segmentId")({
  component: SegmentDetailView,
})

function SegmentDetailView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/m/$segmentId" })
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()

  const [editing, setEditing] = useState(false)

  const back = () =>
    navigate({
      to: "/$orgSlug/$eventSlug/travel",
      params: { orgSlug: params.orgSlug, eventSlug: params.eventSlug },
    })

  const segmentId = decodeURIComponent(params.segmentId)
  const addKind = segmentId.startsWith("add:")
    ? (segmentId.slice(4) as TravelFormKind)
    : null

  const travelQ = useQuery({
    ...eventTravelQueryOptions(eventId ?? ""),
    enabled: !!eventId && !addKind,
  })
  const segments = useMemo(() => buildSegments(travelQ.data), [travelQ.data])
  const segment = useMemo(
    () => segments.find((s) => s.id === segmentId) ?? null,
    [segments, segmentId],
  )

  // Add-travel form (narrow-canvas full screen).
  if (addKind && ADD_KINDS.includes(addKind)) {
    if (!eventId) {
      return (
        <div className="flex h-full flex-col">
          <CanvasHeader title="Travel" icon={<Plane className="h-5 w-5" />} />
          <div className="flex flex-1 items-center justify-center text-plum-700/50 dark:text-plum-200/40">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        </div>
      )
    }
    return (
      <TravelForm
        eventId={eventId}
        initialKind={addKind}
        onClose={back}
        onSaved={back}
        closeVariant="back"
      />
    )
  }

  if (!eventId || travelQ.isLoading) {
    return (
      <div className="flex h-full flex-col">
        <CanvasHeader title="Travel" icon={<Plane className="h-5 w-5" />} />
        <div className="flex flex-1 items-center justify-center text-plum-700/50 dark:text-plum-200/40">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </div>
    )
  }

  if (!segment) {
    return (
      <div className="flex h-full flex-col">
        <CanvasHeader title="Travel" icon={<Plane className="h-5 w-5" />} />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-[14px] font-semibold text-plum-900 dark:text-plum-50">
            Movement not found
          </p>
          <p className="text-[12.5px] text-plum-700/55 dark:text-plum-200/45">
            This travel movement may have been removed.
          </p>
          <button
            onClick={back}
            className="rounded-lg px-3 py-1.5 text-[13px] font-semibold text-white shadow-fluent-sm"
            style={{ background: "var(--accent)" }}
          >
            Back to travel
          </button>
        </div>
      </div>
    )
  }

  if (editing) {
    return (
      <TravelForm
        eventId={eventId}
        segment={segment}
        onClose={() => setEditing(false)}
        onSaved={() => setEditing(false)}
        closeVariant="back"
      />
    )
  }

  return (
    <TravelSegmentDetail
      segment={segment}
      onClose={back}
      onEdit={segment.isStandalone ? undefined : () => setEditing(true)}
      closeVariant="back"
    />
  )
}
